import { Link, useNavigate } from "react-router-dom";
import { LogOut, Sun, User, Palette, Shield, CreditCard, Sparkles, Zap, ArrowRight, Compass, FileText, ExternalLink } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { startWebsiteTour } from "@/lib/tour";

function SettingCard({ icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-6 shadow-sm">
      <div className="mb-5 flex items-center gap-2.5 border-b border-border pb-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-accent/10 text-accent">
          <Icon className="h-4 w-4" />
        </div>
        <h2 className="font-display text-base font-semibold text-foreground">{title}</h2>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl px-1 py-3">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <div className="min-w-0 max-w-full">{children}</div>
    </div>
  );
}

function QuotaMeter({ label, used, limit }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div className="space-y-1.5 py-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-foreground">{label}</span>
        <span className="text-muted-foreground">
          {used} / {limit} used ({limit - used} left)
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-elevated">
        <div
          className={`h-full rounded-full transition-all ${
            pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-accent"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function Settings() {
  const { user, logout } = useAuth();
  const { subscription, openPaywall } = useSubscription();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const quotas = subscription?.quotas || {};
  const usage = subscription?.usage || {};

  return (
    <AppShell title="Settings">
      <div className="mb-8 border-b border-border pb-6">
        <p className="text-xs font-bold uppercase tracking-widest text-accent">Preferences</p>
        <h1 className="mt-2 font-display text-3xl font-bold text-foreground">Settings</h1>
      </div>

      <div className="mx-auto max-w-2xl space-y-5">
        {/* Billing & Subscription */}
        <SettingCard icon={CreditCard} title="Subscription &amp; Billing">
          <Row label="Current Plan">
            <div className="flex items-center gap-2">
              <span
                className={`rounded-full px-3 py-1 text-xs font-bold ${
                  subscription?.active
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
                data-testid="settings-plan-badge"
              >
                {subscription?.plan_name || "No Active Plan"}
              </span>
              <Button
                size="sm"
                variant="outline"
                asChild
                className="h-7 rounded-full text-xs"
              >
                <Link to="/pricing">
                  {subscription?.active ? "Change Plan" : "Choose Plan"}
                </Link>
              </Button>
            </div>
          </Row>

          {subscription?.active && (
            <>
              <Row label="Plan Status">
                <span className="text-xs font-medium text-muted-foreground">
                  {subscription?.is_admin
                    ? "Unlimited Admin Access"
                    : `${subscription?.days_remaining} days remaining`}
                </span>
              </Row>

              {!subscription?.is_admin && (
                <div className="mt-3 border-t border-border pt-3">
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    Plan Usage Quotas
                  </p>
                  <div className="space-y-2">
                    <QuotaMeter
                      label="AI Ideas"
                      used={usage.ideas || 0}
                      limit={quotas.ideas || 0}
                    />
                    <QuotaMeter
                      label="Opening Hooks &amp; Analyses"
                      used={usage.hooks || 0}
                      limit={quotas.hooks || 0}
                    />
                    <QuotaMeter
                      label="Production Scripts"
                      used={usage.scripts || 0}
                      limit={quotas.scripts || 0}
                    />
                    <QuotaMeter
                      label="Content Analyses"
                      used={usage.analysis || 0}
                      limit={quotas.analysis || 0}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {!subscription?.active && (
            <div className="mt-3 rounded-xl border border-accent/30 bg-accent/5 p-4">
              <p className="text-xs font-semibold text-foreground">
                You do not have an active subscription
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Activate a plan (starting at ₹99 for 7 days) to unlock ideas, hooks, scripts, and intelligence.
              </p>
              <Button
                size="sm"
                onClick={() => openPaywall("all features")}
                className="mt-3 rounded-full bg-accent text-white hover:bg-accent-hover text-xs font-bold"
              >
                <Zap className="mr-1.5 h-3.5 w-3.5" /> Subscribe Now
              </Button>
            </div>
          )}
        </SettingCard>

        {/* Account */}
        <SettingCard icon={User} title="Account">
          <Row label="Name">
            <span className="text-sm font-medium text-foreground" data-testid="settings-name">{user?.name}</span>
          </Row>
          <Row label="Email">
            <span className="text-sm font-medium text-foreground" data-testid="settings-email">{user?.email}</span>
          </Row>
          <Row label="Role">
            <span className="rounded-full bg-elevated px-2.5 py-0.5 text-xs font-medium capitalize text-foreground">
              {user?.role}
            </span>
          </Row>
        </SettingCard>

        {/* Appearance */}
        <SettingCard icon={Palette} title="Appearance">
          <Row label="Theme">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-elevated px-3 py-1.5 text-xs font-medium text-foreground">
              <Sun className="h-3.5 w-3.5 text-accent" /> Light — Orange &amp; White
            </span>
          </Row>
        </SettingCard>

        {/* Product Tour & Help */}
        <SettingCard icon={Compass} title="Product Tour &amp; Help">
          <Row label="Interactive Tour">
            <Button
              variant="outline"
              size="sm"
              onClick={() => startWebsiteTour(navigate)}
              className="rounded-full text-xs font-semibold border-border hover:border-accent/40 hover:text-accent"
              data-testid="settings-start-tour"
            >
              <Compass className="mr-1.5 h-3.5 w-3.5 text-accent" /> Start Website Tour
            </Button>
          </Row>
        </SettingCard>

        {/* Legal & Compliance */}
        <SettingCard icon={FileText} title="Legal &amp; Compliance">
          <Row label="Privacy Policy">
            <Link
              to="/privacy"
              className="inline-flex items-center gap-1 text-xs font-semibold text-accent hover:underline"
              data-testid="settings-privacy"
            >
              View Policy <ExternalLink className="h-3 w-3" />
            </Link>
          </Row>
          <Row label="Terms of Service">
            <Link
              to="/terms"
              className="inline-flex items-center gap-1 text-xs font-semibold text-accent hover:underline"
              data-testid="settings-terms"
            >
              View Terms <ExternalLink className="h-3 w-3" />
            </Link>
          </Row>
          <Row label="Cookie Policy">
            <Link
              to="/cookies"
              className="inline-flex items-center gap-1 text-xs font-semibold text-accent hover:underline"
              data-testid="settings-cookies"
            >
              View Cookies <ExternalLink className="h-3 w-3" />
            </Link>
          </Row>
          <Row label="Creator Data Rights">
            <a
              href="mailto:getintouch@viralityai.media?subject=Data%20Export%20or%20Deletion%20Request"
              className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground hover:underline"
            >
              Request Data Action <ExternalLink className="h-3 w-3" />
            </a>
          </Row>
        </SettingCard>

        {/* Session */}
        <SettingCard icon={Shield} title="Session">
          <Row label="Sign out of your Virality workspace">
            <Button variant="outline" size="sm" onClick={doLogout}
              className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
              data-testid="settings-logout">
              <LogOut className="mr-2 h-3.5 w-3.5" /> Log out
            </Button>
          </Row>
        </SettingCard>
      </div>
    </AppShell>
  );
}

